import { Dropdown } from 'components/common'
import { useEffect, useState } from 'react'
import { DropdownInput } from 'types/input'
import { storeFormFields } from './utils'
import { UseFormSetValue } from 'react-hook-form'

const { STORE_LAST_MILE } = storeFormFields

const LastMileDropdown = <T extends Record<string, any>>({
  store,
  setValue,
  ...props
}: DropdownInput<T> & {
  store?: any
  setValue: UseFormSetValue<any>
}) => {
  const initialLastMile = store?.[STORE_LAST_MILE.key]
    ? Object.keys(store[STORE_LAST_MILE.key])[0]
    : undefined

  const [currentLastMile, setCurrentLastMile] = useState<string | undefined>(
    initialLastMile,
  )

  const [loading, setLoading] = useState(true)

  const [aggregatorList, setAggregatorList] = useState<any>({})

  const newSetValue = (_name: any, value: any) => {
    //here this selectedLastMile is only for ui purpose and not for backend as when the dropdown will change it will override the actual lastmile value if we pass the correct key which is lastmile so that's why we have added this selectedLastMile key which will be update and not override the lastmile value we send as we are already handling that part in another useEffct
    setValue('selectedLastMile', value)
    setCurrentLastMile(value)
  }

  const handleInitialAggregatorList = async () => {
    setLoading(true)
    const currentAggregatorLastmile = store?.lastMile as Record<
      string,
      {
        _id: string
        aggregatorName: string
      }[]
    >
    if (currentAggregatorLastmile) {
      // Add metadata: originalGroup
      const withMeta = Object.fromEntries(
        Object.entries(currentAggregatorLastmile).map(([key, value]) => [
          key,
          value.map((v: any) => ({ ...v, originalGroup: key })),
        ]),
      )
      setAggregatorList(withMeta)
    }
    setLoading(false)
  }

  // ✅ Handle aggregator move logic (both directions)
  const handleChangeAggregatorList = (
    item: Record<string, any>,
    fromGroup: string,
  ) => {
    if (!currentLastMile) return

    setAggregatorList((prev: any) => {
      const updated = { ...prev }

      // Remove from its current group
      updated[fromGroup] = updated[fromGroup].filter(
        (agg: any) => agg._id !== item._id,
      )

      // If moved to current group
      if (fromGroup !== currentLastMile) {
        updated[currentLastMile] = [...(updated[currentLastMile] || []), item]
      } else {
        // If moved back (reset)
        const original = item.originalGroup
        updated[original] = [...(updated[original] || []), item]
      }

      return updated
    })
  }

  // ✅ Derive other aggregator groups
  const otherAggregatorGroups = Object.keys(aggregatorList).filter(
    (key) => key !== currentLastMile,
  )

  useEffect(() => {
    handleInitialAggregatorList()
  }, [store])

  useEffect(() => {
    if (Object.keys(aggregatorList).length) {
      setValue('lastMile', aggregatorList)
    }
  }, [aggregatorList])

  return (
    <div className=" grid grid-cols-3 gap-5">
      <Dropdown {...props} setValue={newSetValue} className="" />
      <div className="col-span-2 rounded-md border border-gray-200 bg-gray-50 p-4 shadow-sm">
        {currentLastMile && (
          <>
            {/* Current Aggregator Section */}
            <h4 className="mb-2 text-lg font-semibold text-gray-700">
              {currentLastMile} Aggregators
            </h4>
            <div className="flex flex-wrap gap-2">
              {aggregatorList[currentLastMile]?.length ? (
                aggregatorList[currentLastMile].map((item: any, index: any) => (
                  <span
                    key={index}
                    onClick={() =>
                      item.originalGroup !== currentLastMile &&
                      handleChangeAggregatorList(item, currentLastMile)
                    }
                    className="cursor-pointer rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-200"
                    title="Click to reset"
                  >
                    {item.aggregatorName}{' '}
                    {item.originalGroup !== currentLastMile && '⟲'}
                  </span>
                ))
              ) : loading ? (
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-6 w-24 animate-pulse rounded-full bg-gray-300"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  No aggregators found under {currentLastMile}
                </p>
              )}
            </div>

            {/* Divider */}
            <hr className="my-4 border-gray-300" />

            {/* Other Aggregator Section */}
            <h4 className="mb-2 text-lg font-semibold text-gray-700">
              Other Aggregators
            </h4>
            {otherAggregatorGroups.length ? (
              otherAggregatorGroups.map((groupKey) => (
                <div key={groupKey} className="mb-3">
                  <p className="mb-1 text-sm font-semibold text-gray-600">
                    {groupKey}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {aggregatorList[groupKey]?.length ? (
                      aggregatorList[groupKey].map((item: any, index: any) => (
                        <span
                          key={index}
                          onClick={() =>
                            handleChangeAggregatorList(item, groupKey)
                          }
                          className="cursor-pointer rounded-full bg-gray-200 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-300"
                          title={`Move to ${currentLastMile}`}
                        >
                          {item.aggregatorName}
                        </span>
                      ))
                    ) : (
                      <p className="ml-2 text-sm text-gray-400">
                        No aggregators available
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : loading ? (
              <div className="ml-2 flex flex-wrap gap-2">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-6 w-20 animate-pulse rounded-full bg-gray-300"
                  />
                ))}
              </div>
            ) : (
              <p className="ml-2 text-sm text-gray-400">
                No aggregators available
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default LastMileDropdown
